import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { Send, CheckCircle, Loader, Clock, Video } from 'lucide-react';

const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_AUTOREPLY_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID;

export default function Contact() {
  const formRef = useRef(null);
  const sectionRef = useRef(null);
  const formOpenedAt = useRef(Date.now());
  const [formData, setFormData] = useState({ name: '', pronoun: '', email: '', phone: '', message: '', preferred_time: '', consent: false, website: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll('.reveal').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const validate = () => {
    const errs = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) errs.name = 'Please enter your name';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Please enter a valid email';
    if (!formData.message.trim() || formData.message.trim().length < 10) errs.message = 'Please write a brief message';
    if (!formData.consent) errs.consent = 'Please confirm you consent to being contacted';
    return errs;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot — if the hidden "website" field is filled, silently drop the submission (it's a bot)
    if (formData.website) { setStatus('success'); return; }
    // Time-based guard — humans take more than ~2s to fill a form. Bots are instant.
    if (Date.now() - formOpenedAt.current < 2000) { setStatus('success'); return; }
    // Per-browser rate limit — max 3 submissions per hour to deter abuse without a CAPTCHA
    try {
      const KEY = 'dg_contact_submissions';
      const HOUR = 60 * 60 * 1000;
      const now = Date.now();
      const recent = JSON.parse(localStorage.getItem(KEY) || '[]').filter((t) => now - t < HOUR);
      if (recent.length >= 3) {
        setErrors({ form: 'You have sent several messages recently. Please email donnagordonpsychotherapy@gmail.com directly if you need urgent support.' });
        return;
      }
      localStorage.setItem(KEY, JSON.stringify([...recent, now]));
    } catch (_) { /* localStorage may be disabled — fail open */ }

    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setStatus('loading');

    const templateParams = {
      from_name: formData.name,
      from_pronoun: formData.pronoun,
      from_email: formData.email,
      from_phone: formData.phone || 'Not provided',
      message: formData.message,
      preferred_time: formData.preferred_time || 'Not specified',
      to_email: 'donnagordonpsychotherapy@gmail.com',
      reply_to: formData.email,
      time: new Date().toLocaleString('en-IE'),
    };

    if (EMAILJS_PUBLIC_KEY && EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && !EMAILJS_PUBLIC_KEY.startsWith('YOUR_')) {
      try {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, { publicKey: EMAILJS_PUBLIC_KEY });
        if (EMAILJS_AUTOREPLY_TEMPLATE_ID && !EMAILJS_AUTOREPLY_TEMPLATE_ID.startsWith('YOUR_')) {
          await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_AUTOREPLY_TEMPLATE_ID, { ...templateParams, to_name: formData.name }, { publicKey: EMAILJS_PUBLIC_KEY });
        }
      } catch (err) {
        console.warn('EmailJS error:', err);
      }
    }

    setStatus('success');
    setFormData({ name: '', pronoun: '', email: '', phone: '', message: '', preferred_time: '', consent: false, website: '' });
  };

  const inputClass = (field) =>
    `w-full font-body text-sm text-[#2C362A] bg-white border rounded-xl px-4 py-3 outline-none transition-all duration-200 placeholder:text-[#A8B5A3] focus:ring-2 focus:ring-[#708A72]/30 focus:border-[#708A72] ${
      errors[field] ? 'border-red-300' : 'border-[#D1D8D0]'
    }`;

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#F2F4ED]" data-testid="contact-section" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto">
          <div className="mb-10 reveal">
            <h2 className="font-heading text-4xl md:text-5xl text-[#2C362A] leading-tight mb-4">
              Get in Touch
            </h2>
            <p className="font-body text-base text-[#5B6E56] leading-relaxed mb-6">
              You're not alone in this. Support is here whenever it feels right to reach out.
            </p>

            {/* Session info badges */}
            <div className="flex flex-wrap gap-3" data-testid="session-info-badges">
              <span
                className="inline-flex items-center gap-2 bg-white border border-[#D1D8D0] text-[#2C362A] font-body text-sm px-4 py-2 rounded-full shadow-sm"
                data-testid="session-length-badge"
              >
                <Clock size={14} strokeWidth={1.5} className="text-[#708A72]" />
                50‑minute sessions
              </span>
              <span
                className="inline-flex items-center gap-2 bg-white border border-[#D1D8D0] text-[#2C362A] font-body text-sm px-4 py-2 rounded-full shadow-sm"
                data-testid="session-mode-badge"
              >
                <Video size={14} strokeWidth={1.5} className="text-[#708A72]" />
                In‑person &amp; online
              </span>
            </div>
          </div>

          {status === 'success' && (
            <div className="mb-6 flex items-start gap-3 bg-white border border-[#D1D8D0] rounded-2xl p-5 reveal visible" data-testid="contact-success">
              <CheckCircle size={20} strokeWidth={1.5} className="text-[#708A72] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-body font-semibold text-sm text-[#2C362A] mb-1">Message received</p>
                <p className="font-body text-sm text-[#5B6E56]">
                  Thank you for reaching out. Donna will be in touch with you soon. Take good care of yourself in the meantime.
                </p>
              </div>
            </div>
          )}

          {status !== 'success' && (
            <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-5 reveal" data-testid="contact-form">
              {errors.form && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="contact-form-error">
                  {errors.form}
                </div>
              )}

              {/* Full Name */}
              <div>
                <label htmlFor="contact-name" className="block font-body text-xs font-semibold tracking-wide text-[#5B6E56] uppercase mb-1.5">
                  Full Name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Tell us your full name"
                  className={inputClass('name')}
                  data-testid="contact-name-input"
                  disabled={status === 'loading'}
                />
                {errors.name && <p className="font-body text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>

              {/* Pronouns */}
              <div>
                <label htmlFor="contact-pronoun" className="block font-body text-xs font-semibold tracking-wide text-[#5B6E56] uppercase mb-1.5">
                  Pronouns <span className="normal-case font-normal text-[#A8B5A3]">(optional)</span>
                </label>
                <select
                  id="contact-pronoun"
                  name="pronoun"
                  value={formData.pronoun}
                  onChange={handleChange}
                  className={inputClass('pronoun')}
                  data-testid="contact-pronoun-select"
                  disabled={status === 'loading'}
                >
                  <option value="">Select your pronouns...</option>
                  <option value="She/Her">She/Her</option>
                  <option value="He/Him">He/Him</option>
                  <option value="They/Them">They/Them</option>
                </select>
              </div>

              {/* Honeypot — hidden from humans, irresistible to bots */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  type="text"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="contact-email" className="block font-body text-xs font-semibold tracking-wide text-[#5B6E56] uppercase mb-1.5">
                  Email Address
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={inputClass('email')}
                  data-testid="contact-email-input"
                  disabled={status === 'loading'}
                />
                {errors.email && <p className="font-body text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="contact-phone" className="block font-body text-xs font-semibold tracking-wide text-[#5B6E56] uppercase mb-1.5">
                  Phone Number
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g. +353 89 000 0000"
                  className={inputClass('phone')}
                  data-testid="contact-phone-input"
                  disabled={status === 'loading'}
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="contact-message" className="block font-body text-xs font-semibold tracking-wide text-[#5B6E56] uppercase mb-1.5">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Share a little about what brings you here, if you'd like..."
                  rows={5}
                  className={`${inputClass('message')} resize-none`}
                  data-testid="contact-message-input"
                  disabled={status === 'loading'}
                />
                {errors.message && <p className="font-body text-xs text-red-500 mt-1">{errors.message}</p>}
              </div>

              {/* Best days/times */}
              <div>
                <label htmlFor="contact-time" className="block font-body text-xs font-semibold tracking-wide text-[#5B6E56] uppercase mb-1.5">
                  Best Days / Times to Call <span className="normal-case font-normal text-[#708A72]">(For a free introductory call)</span>
                </label>
                <input
                  id="contact-time"
                  type="text"
                  name="preferred_time"
                  value={formData.preferred_time}
                  onChange={handleChange}
                  placeholder="e.g. Weekday mornings, Tuesday afternoons..."
                  className={inputClass('preferred_time')}
                  data-testid="contact-time-input"
                  disabled={status === 'loading'}
                />
              </div>

              {/* GDPR Consent — required */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="contact-consent-label">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    disabled={status === 'loading'}
                    className="mt-1 h-4 w-4 rounded border-[#D1D8D0] text-[#708A72] focus:ring-2 focus:ring-[#708A72]/30 cursor-pointer flex-shrink-0"
                    data-testid="contact-consent-checkbox"
                  />
                  <span className="font-body text-sm text-[#5B6E56] leading-relaxed">
                    I consent to being contacted regarding my enquiry and understand my data will be processed in accordance with the{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#708A72] underline underline-offset-2 hover:no-underline transition-all"
                      data-testid="contact-privacy-link"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
                {errors.consent && <p className="font-body text-xs text-red-500 mt-1.5 ml-7" data-testid="contact-consent-error">{errors.consent}</p>}
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#708A72] text-white font-body font-medium text-sm px-8 py-3.5 rounded-full hover:bg-[#5D755F] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                data-testid="contact-submit-btn"
              >
                {status === 'loading' ? (
                  <><Loader size={15} strokeWidth={1.5} className="animate-spin" />Sending...</>
                ) : (
                  <><Send size={15} strokeWidth={1.5} />Send Message</>
                )}
              </button>

              <p className="font-body text-xs text-[#708A72] italic leading-relaxed">
                Your message is confidential and will only be read by Donna. The information you submit through this form will only be used to respond to your enquiry. Please avoid sharing highly sensitive medical or mental health information in this initial contact form.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
